import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/ui/form";
import { Input } from "@/ui/input";
import { MaterialIcon } from "@/ui/material-icon";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/ui/select";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/ui/tooltip";
import { useAutoFillQuery } from "@/features/adp/hooks/useAutoFillQuery";
import { applyValidator } from "./schemaDefinitionFormValidators";

export type SchemaDefinitionField = {
    type?: string;
    key?: string;
    label?: string;
    placeholder?: string;
    toolTip?: string;
    validators?: string[];
    options?: string[];
    show?: boolean;
    defaultValue?: string | number;
    order?: number;
    accept?: string;
};

type SchemaDefinitionFormProps = {
    dbName?: string;
    type: 'source' | 'destination';
    nodeId?: string;
    fields: SchemaDefinitionField[];
    initialValues?: Record<string, string>;
    submitLabel?: string;
    submitDisabled?: boolean;
    showSubmitButton?: boolean;
    readOnly?: boolean;
    fileAccept?: string;
    onValidityChange?: (isValid: boolean) => void;
    onValuesChange?: (values: Record<string, string>) => void;
    onSubmit: (values: Record<string, string>) => void;
    onFileChange?: (key: string, file: File | null) => void;
};

const normalizeValue = (value: unknown): string => {
    if (value == null) return "";
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    return "";
};

const getDefaultValues = (
    fields: SchemaDefinitionField[],
    initialValues?: Record<string, string>,
) => {
    const defaults: Record<string, string> = {};
    fields.forEach((field) => {
        const key = field.key ?? "";
        if (!key) return;
        const defaultFromSchema = normalizeValue(field.defaultValue);
        const initial = normalizeValue(initialValues?.[key]);
        defaults[key] = initial || defaultFromSchema;
    });
    return defaults;
};

const resolveInputType = (
    isPasswordField: boolean,
    isVisible: boolean,
    configuredType?: string,
): string => {
    if (isPasswordField) {
        return isVisible ? "text" : "password";
    }
    return configuredType || "text";
};

const buildZodShape = (fields: SchemaDefinitionField[]) => {
    const shape: Record<string, z.ZodTypeAny> = {};

    fields.forEach((field) => {
        const key = field.key ?? "";
        if (!key) return;
        const label = field.label ?? key;
        const validators = field.validators ?? [];
        const isRequired = validators.includes("required");
        let fieldSchema = z.string();

        validators.forEach((validatorName) => {
            fieldSchema = applyValidator(validatorName, fieldSchema, label);
        });

        shape[key] = isRequired ? fieldSchema : z.union([z.literal(""), fieldSchema]).optional();
    });

    return z.object(shape);
};

const SchemaDefinitionForm = ({
    dbName,
    fields,
    initialValues,
    onSubmit,
    submitLabel = "Save",
    submitDisabled = false,
    showSubmitButton = true,
    readOnly = false,
    fileAccept,
    onValidityChange,
    onValuesChange,
    onFileChange,
    type,
    nodeId,
}: SchemaDefinitionFormProps) => {
    const visibleFields = useMemo(
        () =>
            (fields ?? [])
                .filter((field) => field?.show !== false && !!field?.key)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [fields],
    );

    const defaultValues = useMemo(
        () => getDefaultValues(visibleFields, initialValues),
        [initialValues, visibleFields],
    );

    const formSchema = useMemo(() => buildZodShape(visibleFields), [visibleFields]);

    const form = useForm<Record<string, string>>({
        resolver: zodResolver(formSchema),
        defaultValues,
        mode: "onChange",
    });
    const hasSourceNameField = useMemo(
        () => visibleFields.some((field) => field.key === "source_name"),
        [visibleFields],
    );
    const autoFillBaseName = useMemo(() => {
        const normalizedDbName = (dbName ?? "").trim();
        if (!normalizedDbName) return "";

        const safeName = normalizedDbName.replaceAll(/\s+/g, "_");

        return `${safeName}_${type === "source" ? "source" : "dest"}`;
    }, [dbName, type]);
    const autoFillTableName = type === "source" ? "source_data_source" : "target_data_source";
    const { data: autoFillData } = useAutoFillQuery({
        baseName: autoFillBaseName,
        tableName: autoFillTableName,
        enabled: hasSourceNameField && !readOnly && !!nodeId,
        nodeId,
    });

    const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const currentValues = form.getValues();
        form.reset({
            ...defaultValues,
            ...currentValues,
        });
    }, [defaultValues, form]);

    useEffect(() => {
        onValidityChange?.(form.formState.isValid);
    }, [form.formState.isValid, onValidityChange]);

    useEffect(() => {
        const subscription = form.watch((values) => {
            onValuesChange?.(values as Record<string, string>);
        });
        return () => subscription.unsubscribe();
    }, [form, onValuesChange]);

    useEffect(() => {
        if (!hasSourceNameField) return;
        const suggestedName = autoFillData?.suggestedName?.trim();
        if (!suggestedName) return;

        const currentValue = (form.getValues("source_name") ?? "").trim();
        const sourceNameState = form.getFieldState("source_name");
        if (currentValue || sourceNameState.isDirty) return;

        form.setValue("source_name", suggestedName, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: true,
        });
    }, [autoFillData?.suggestedName, form, hasSourceNameField]);

    type FieldMeta = { isPasswordField: boolean; isSelectField: boolean; isFileField: boolean; inputType: string };

    const renderFieldControl = (
        schemaField: SchemaDefinitionField,
        field: { value: string; onChange: (value: unknown) => void },
        fieldKey: string,
        fieldLabel: string,
        meta: FieldMeta,
    ) => {
        const { isPasswordField, isSelectField, isFileField, inputType } = meta;
        if (isFileField) {
            return (
                <Input
                    type="file"
                    accept={schemaField.accept ?? fileAccept}
                    disabled={readOnly}
                    className="cursor-pointer"
                    onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        field.onChange(file?.name ?? "");
                        onFileChange?.(fieldKey, file);
                    }}
                />
            );
        }

        if (isSelectField) {
            return (
                <Select
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                    disabled={readOnly}
                >
                    <SelectTrigger>
                        <SelectValue placeholder={schemaField.placeholder || `Select ${fieldLabel}`} />
                    </SelectTrigger>
                    <SelectContent>
                        {(schemaField.options ?? []).map((option) => (
                            <SelectItem key={option} value={option}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        }

        return (
            <div className="relative">
                <Input
                    {...field}
                    value={field.value ?? ""}
                    type={inputType}
                    placeholder={schemaField.placeholder || `Enter ${fieldLabel}`}
                    autoComplete={isPasswordField ? "new-password" : "off"}
                    readOnly={readOnly}
                    disabled={readOnly}
                />
                {isPasswordField && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2"
                        onClick={() =>
                            setPasswordVisibility((prev) => ({
                                ...prev,
                                [fieldKey]: !prev[fieldKey],
                            }))
                        }
                        disabled={readOnly}
                    >
                        <MaterialIcon
                            name={passwordVisibility[fieldKey] ? "visibility_off" : "visibility"}
                            size={18}
                        />
                    </Button>
                )}
            </div>
        );
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-4">
                {visibleFields.map((schemaField) => {
                    const fieldKey = schemaField.key;
                    if (!fieldKey) return null;
                    const fieldLabel = schemaField.label ?? fieldKey;
                    const isRequired = schemaField.validators?.includes("required") ?? false;
                    const isSelectField = schemaField.type === "select" || schemaField.type === "matselect";
                    const isPasswordField = schemaField.type === "password";
                    const isFileField = schemaField.type === "file";
                    const inputType = resolveInputType(
                        isPasswordField,
                        passwordVisibility[fieldKey] === true,
                        schemaField.type,
                    );

                    return (
                        <FormField
                            key={fieldKey}
                            control={form.control}
                            name={fieldKey}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <span>{fieldLabel}</span>
                                        {!isRequired && <span className="text-muted-foreground">(optional)</span>}
                                        {schemaField.toolTip && (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span className="inline-flex cursor-help text-muted-foreground">
                                                        <MaterialIcon name="info" size={14} />
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>{schemaField.toolTip}</TooltipContent>
                                            </Tooltip>
                                        )}
                                    </FormLabel>

                                    <FormControl>
                                        {renderFieldControl(
                                            schemaField,
                                            field,
                                            fieldKey,
                                            fieldLabel,
                                            { isPasswordField, isSelectField, isFileField, inputType },
                                        )}
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    );
                })}

                {showSubmitButton && (
                    <Button type="submit" className="w-full" variant="outline" disabled={submitDisabled}>
                        {submitLabel}
                    </Button>
                )}
            </form>
        </Form>
    );
};

export default SchemaDefinitionForm;
